import React, { useEffect, useState } from 'react';
import { MessageOutlined } from '@ant-design/icons';
import { useTranslation } from '../../i18n';
import type { ChatMessage, ChatParticipant, ChatStatusLabels, QuickReply } from './chatTypes';
import { ChatComposer } from './ChatComposer';
import { ChatHeader } from './ChatHeader';
import { ChatMessageList } from './ChatMessageList';
import { QuickReplyBar } from './QuickReplyBar';

export type ChatPanelProps = {
  /** Changing this resets the draft and scroll position, e.g. the conversation id. */
  conversationKey?: string;
  peer?: ChatParticipant;
  self: ChatParticipant;
  messages: ChatMessage[];
  headerSubtitle?: React.ReactNode;
  headerActions?: React.ReactNode;
  /** Rendered between the header and the message list, e.g. stats chips. */
  toolbar?: React.ReactNode;
  quickReplies?: QuickReply[];
  hasMore?: boolean;
  loadingEarlier?: boolean;
  onLoadEarlier?: () => void;
  onSend: (text: string) => void | Promise<void>;
  onResend?: (message: ChatMessage) => void;
  sendDisabledReason?: string;
  sending?: boolean;
  onPickImage?: (file: File) => void;
  onPickFile?: (file: File) => void;
  onOpenTemplate?: () => void;
  emptyText?: React.ReactNode;
  idleText?: React.ReactNode;
  statusLabels?: ChatStatusLabels;
  composerPlaceholder?: string;
  sendLabel?: React.ReactNode;
  submitOn?: 'enter' | 'modEnter' | 'ctrlNewline';
  composerExtraActions?: React.ReactNode;
  composerLayout?: 'inline' | 'stacked';
};

export function ChatPanel({
  conversationKey,
  peer,
  self,
  messages,
  headerSubtitle,
  headerActions,
  toolbar,
  quickReplies = [],
  hasMore,
  loadingEarlier,
  onLoadEarlier,
  onSend,
  onResend,
  sendDisabledReason,
  sending,
  onPickImage,
  onPickFile,
  onOpenTemplate,
  emptyText,
  idleText,
  statusLabels,
  composerPlaceholder,
  sendLabel,
  submitOn,
  composerExtraActions,
  composerLayout,
}: ChatPanelProps) {
  const { t } = useTranslation();
  const resolvedIdle = idleText ?? t('chat.idle');
  const [draft, setDraft] = useState('');

  useEffect(() => {
    setDraft('');
  }, [conversationKey]);

  if (!peer) {
    return (
      <div className="flex h-full min-h-0 flex-col items-center justify-center gap-2 bg-[#f7f8fb] text-sm text-slate-400">
        <MessageOutlined className="text-3xl text-slate-300" />
        {resolvedIdle}
      </div>
    );
  }

  const handleSend = async (text: string) => {
    try {
      await onSend(text);
      setDraft('');
    } catch {
      // 发送失败时保留输入，由页面提示原因。
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <ChatHeader peer={peer} subtitle={headerSubtitle} actions={headerActions} />
      {toolbar}
      <ChatMessageList
        messages={messages}
        self={self}
        peer={peer}
        hasMore={hasMore}
        loadingEarlier={loadingEarlier}
        onLoadEarlier={onLoadEarlier}
        onResend={onResend}
        emptyText={emptyText}
        statusLabels={statusLabels}
      />
      <QuickReplyBar
        items={quickReplies}
        disabled={Boolean(sendDisabledReason)}
        onPick={(text) => setDraft(text)}
      />
      <ChatComposer
        value={draft}
        onChange={setDraft}
        onSend={handleSend}
        disabledReason={sendDisabledReason}
        sending={sending}
        onPickImage={onPickImage}
        onPickFile={onPickFile}
        onOpenTemplate={onOpenTemplate}
        placeholder={composerPlaceholder}
        sendLabel={sendLabel}
        submitOn={submitOn}
        extraActions={composerExtraActions}
        layout={composerLayout}
      />
    </div>
  );
}
